import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import HeadsImage from '../../assets/heads.png';
import TailsImage from '../../assets/tails.png';
import ProfileImage from '../../assets/profile.png';
import SolanaLogo from '../common/SolanaLogo';
import SimpleCoin3D from '../common/SimpleCoin3D';
import api from '../../services/api';
import { priceService } from '../../services/priceService';
import { showError } from '../../utils/toast';
import { socketService } from '../../services/SocketService';
import { getProfileImageSrc } from '../../utils/profileUtils';
import ErrorBoundary from '../common/ErrorBoundary';
import './CoinflipPopup.css';

interface Player {
  id: string;
  username: string;
  choice: 'heads' | 'tails';
  avatar?: string;
}

interface GameInfo {
  id: string;
  player1: Player;
  player2?: Player;
  amount: number;
  status: 'waiting' | 'playing' | 'finished';
}

interface CoinflipPopupProps {
  isOpen: boolean;
  onClose: () => void;
  gameInfo: GameInfo | null;
  currentUserId?: string;
}

interface GameResult {
  outcome: 'heads' | 'tails';
  won: boolean;
  payout: string | number;
  winnerId?: string;
  serverSeedHash?: string;
  nonce?: number;
}

type AnimationPhase = 'waiting' | 'calling-bot' | 'spinning' | 'mining' | 'revealing' | 'finished';

const CoinflipPopup: React.FC<CoinflipPopupProps> = ({ 
  isOpen, 
  onClose, 
  gameInfo, 
  currentUserId 
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('waiting');
  const [result, setResult] = useState<GameResult | null>(null);
  const [solPrice, setSolPrice] = useState<number>(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [hasFlipped, setHasFlipped] = useState(false);
  const [liveGameInfo, setLiveGameInfo] = useState<GameInfo | null>(gameInfo);
  const [isJoining, setIsJoining] = useState(false);
  const [isCallingBot, setIsCallingBot] = useState(false);
  
  // Robust polling for game info while popup is open and game is not finished
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (isOpen && liveGameInfo && liveGameInfo.status !== 'finished') {
      const gameId = liveGameInfo.id;
      interval = setInterval(async () => {
        try {
          const updated = await api.games.getGameDetails(gameId);
          if (!updated) return;
          
          setLiveGameInfo(prev => {
            if (!prev) return updated;
            
            // Use backend data as the source of truth, but ensure choices are preserved if missing
            return {
              ...updated,
              player1: {
                ...updated.player1,
                choice: updated.player1.choice || prev.player1.choice
              },
              player2: updated.player2 ? {
                ...updated.player2,
                choice: updated.player2.choice || prev.player2?.choice || (updated.player1.choice === 'heads' ? 'tails' : 'heads')
              } : undefined
            };
          });
        } catch {}
      }, 3000);
    }
    return () => interval && clearInterval(interval);
    // Only depend on the extracted values, not the whole liveGameInfo object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, liveGameInfo?.id, liveGameInfo?.status]);
  // Subscribe to real-time coinflip updates and sync gameInfo
  useEffect(() => {
    if (!isOpen || !gameInfo) return;
    
    // Initial sync with gameInfo - use backend data directly
    setLiveGameInfo(prev => {
      if (!prev || prev.id !== gameInfo.id) {
        return gameInfo;
      }
      return prev;
    });

    // Socket subscription setup
    const unsubscribe = socketService.subscribeToCoinflipUpdates((data) => {
      if (data.type === 'game-completed' && data.gameInfo?.id === gameInfo.id) {
        console.log('Game completed socket event received:', data);
        // Use the gameInfo from socket as the source of truth
        setLiveGameInfo(data.gameInfo);
        
        if (data.result) {
          // Check if this user is a spectator (not the one who triggered the action)
          const isSpectator = !isJoining && !isCallingBot;
          
          if (isSpectator && !hasFlipped) {
            // Trigger animation for spectators
            console.log('Triggering animation for spectator');
            setHasFlipped(true);
            setIsFlipping(true);
            setResult(data.result);
            setAnimationPhase('spinning');
          } else {
            // For the player who triggered the action, animation is already running
            // Just set the result and let handleFlipComplete finish
            setResult(data.result);
            if (animationPhase === 'waiting') {
              setAnimationPhase('finished');
            }
          }
        }
      }
    });

    // Join coinflip room for updates
    socketService.joinCoinflipRoom();

    return () => {
      unsubscribe();
      socketService.leaveCoinflipRoom();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, gameInfo]);
  

  // Reset state when game changes or when a new waiting game is opened
  useEffect(() => {
    if (gameInfo) {
      // If this is a different game or a waiting game, reset all state
      if (gameInfo.status === 'waiting') {
        setHasFlipped(false);
        setIsFlipping(false);
        setResult(null);
        setAnimationPhase('waiting');
      } else if (gameInfo.status === 'finished' && result) {
        setAnimationPhase('finished');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameInfo?.id, gameInfo?.status]); // Only depend on id and status to trigger reset

  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const price = await priceService.getSOLPrice();
        setSolPrice(price);
      } catch (error) {
        console.error('Failed to fetch SOL price:', error);
      }
    };

    fetchSolPrice();
  }, []);

  const handleCallBot = async () => {
    if (!liveGameInfo || isCallingBot) return;

    // Get latest game state before proceeding
    try {
      const latestGameInfo = await api.games.getGameDetails(liveGameInfo.id);
      if (!latestGameInfo) {
        showError('Game not found');
        return;
      }

      // Validate game state with latest info
      if (latestGameInfo.status !== 'waiting') {
        showError('This game is no longer available');
        return;
      }

      if (latestGameInfo.player2) {
        showError('This game already has a second player');
        return;
      }

      setIsCallingBot(true);
      setAnimationPhase('calling-bot');

      // Call bot with proper error handling
      const result = await api.games.callBot(latestGameInfo.id);
      if (!result) {
        throw new Error('Failed to get game result from bot');
      }

      setAnimationPhase('spinning');
      
      // Update game state with bot info before starting animation
      const updatedGameInfo = await api.games.getGameDetails(latestGameInfo.id);
      if (updatedGameInfo) {
        setLiveGameInfo(updatedGameInfo);
      }

      startCoinFlipSequence(Promise.resolve(result));
    } catch (error: any) {
      console.error('Call bot error:', error);
      showError(error.message || 'Failed to call bot');
      setAnimationPhase('waiting');
    } finally {
      setIsCallingBot(false);
    }
  };

  const startCoinFlipSequence = async (gameResultPromise?: Promise<GameResult | null>) => {
    // Prevent double flips
    if (hasFlipped || isFlipping) {
      return;
    }
    
    setAnimationPhase('spinning');
    
    // First, get the result to know what we're animating towards
    let gameResult: GameResult | null = null;
    
    try {
      gameResult = gameResultPromise ? await gameResultPromise : null;
      
      if (gameResult === null) {
        setAnimationPhase('waiting');
        return;
      }
    } catch (error: any) {
      showError('An error occurred. Please try again.');
      setAnimationPhase('waiting');
      return;
    }

    // Start the 3D coin flip animation
    setHasFlipped(true);
    setIsFlipping(true);
    setResult(gameResult);
  };

  const handleFlipComplete = () => {
    // Show winner text after coin lands
    setTimeout(() => {
      setAnimationPhase('finished');
      setIsFlipping(false);
      setIsJoining(false);
    }, 1000);
  };

  const handleJoinGame = async () => {
    if (!liveGameInfo || !user || isJoining) return;

    // Validate game state
    const latestGameInfo = await api.games.getGameDetails(liveGameInfo.id);
    if (!latestGameInfo || latestGameInfo.status !== 'waiting') {
      showError('This game is no longer available to join');
      return;
    }

    if (latestGameInfo.player2) {
      showError('This game already has a second player');
      return;
    }

    // Validate user has sufficient balance
    if (user.balance < latestGameInfo.amount) {
      showError(`Insufficient balance. You need ${(latestGameInfo.amount / 1000000000).toFixed(3)} SOL but only have ${(user.balance / 1000000000).toFixed(3)} SOL`);
      return;
    }

    setIsJoining(true);
    setAnimationPhase('spinning');
    
    // Start the join game API request and handle errors immediately
    const gameResultPromise = api.games.joinCoinflip({
      gameId: liveGameInfo.id,
      clientSeed: Math.random().toString(36).substring(2, 15)
    }).then(async (res) => {
      // Refresh game info after join
      const updated = await api.games.getGameDetails(liveGameInfo.id);
      setLiveGameInfo(updated);
      return res;
    }).catch((error: any) => {
      const errorMessage = error?.message || error?.error || 'Failed to join game';
      showError(errorMessage);
      setAnimationPhase('waiting');
      setIsJoining(false);
      return null;
    });
    
    startCoinFlipSequence(gameResultPromise);
  };

  const getWinnerDisplay = () => {
    if (!result || !liveGameInfo || animationPhase !== 'finished') return null;

    // Determine winner based on backend result
    const winnerId = result.winnerId;
    
    let winnerName = '';
    
    // Use backend winnerId to determine the correct winner name
    if (winnerId === 'house' || winnerId === 'bot') {
      winnerName = 'Bot';
    } else if (winnerId === liveGameInfo.player1.id) {
      winnerName = liveGameInfo.player1.username;
    } else if (liveGameInfo.player2 && winnerId === liveGameInfo.player2.id) {
      winnerName = liveGameInfo.player2.username;
    } else {
      // Fallback - this shouldn't happen if backend is working correctly
      console.warn('Winner ID not found in players:', winnerId, liveGameInfo);
      winnerName = 'Unknown';
    }

    // Calculate display payout
    let displayPayout = 0;
    
    // Calculate the winner's payout (regardless of who is viewing)
    const betAmountInLamports = (result as any).betAmount || liveGameInfo.amount;
    const expectedWinnerPayout = (betAmountInLamports * 1.9) / 1000000000;
    
    // Always show the winner's payout amount
    if (result.payout !== undefined && result.payout !== null && Number(result.payout) > 0) {
      // Use the actual payout from backend if available and non-zero
      displayPayout = Number(result.payout) / 1000000000;
    } else {
      // Fallback: calculate expected payout (bet * 1.9)
      displayPayout = expectedWinnerPayout;
    }

    return (
      <div className="winner-display">
        <h3 className="winner-text">
          {winnerName} won {displayPayout.toFixed(3)} SOL
          {solPrice > 0 && (
            <span className="usd-amount"> (${(displayPayout * solPrice).toFixed(2)})</span>
          )}
        </h3>
      </div>
    );
  };

  if (!isOpen || !liveGameInfo) return null;

  return (
    <ErrorBoundary>
      <div className="coinflip-popup-overlay" onClick={onClose}>
      <div className="coinflip-popup" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <h2>Coinflip Battle</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="popup-body">
          <div className="game-amount">
            <SolanaLogo className="amount-icon" width={20} height={20} />
            <span className="amount-text">
              {(liveGameInfo.amount / 1000000000).toFixed(3)} SOL
              {solPrice > 0 && (
                <span className="usd-amount"> (${((liveGameInfo.amount / 1000000000) * solPrice).toFixed(2)})</span>
              )}
            </span>
          </div>

          <div className="players-section">
            {/* Player 1 */}
            <div className={`player-display ${result && animationPhase === 'finished' && result.winnerId === liveGameInfo.player1.id ? 'winner' : ''}`}>
              <div className="player-avatar-container">
                <img
                  src={getProfileImageSrc(liveGameInfo.player1.avatar, liveGameInfo.player1.username)}
                  alt={liveGameInfo.player1.username}
                  className="player-avatar"
                  onError={(e) => {
                    e.currentTarget.src = ProfileImage;
                  }}
                />
                <div className="choice-indicator">
                  <img 
                    src={liveGameInfo.player1.choice === 'heads' ? HeadsImage : TailsImage} 
                    alt={liveGameInfo.player1.choice} 
                    className="choice-coin"
                  />
                </div>
              </div>
              <div className="player-name">{liveGameInfo.player1.username}</div>
              <div className="player-choice">
                {liveGameInfo.player1.choice?.toUpperCase() || ''}
              </div>
            </div>

            {/* Coin Display */}
            <div className="coin-display">
              <SimpleCoin3D 
                isFlipping={isFlipping}
                result={result?.outcome}
                onFlipComplete={handleFlipComplete}
                size={80}
              />
            </div>

            {/* Player 2 / House */}
            <div className={`player-display ${result && animationPhase === 'finished' && (result.winnerId === liveGameInfo.player2?.id || result.winnerId === 'house' || result.winnerId === 'bot') ? 'winner' : ''}`}>
              {liveGameInfo.player2 ? (
                <>
                  <div className="player-avatar-container">
                    <img
                      src={getProfileImageSrc(liveGameInfo.player2.avatar, liveGameInfo.player2.username)}
                      alt={liveGameInfo.player2.username}
                      className="player-avatar"
                      onError={(e) => {
                        e.currentTarget.src = ProfileImage;
                      }}
                    />
                    <div className="choice-indicator">
                      <img 
                        src={liveGameInfo.player2.choice === 'heads' ? HeadsImage : TailsImage} 
                        alt={liveGameInfo.player2.choice} 
                        className="choice-coin"
                      />
                    </div>
                  </div>
                  <div className="player-name">
                    {liveGameInfo.player2.username}
                  </div>
                  <div className="player-choice">
                    {liveGameInfo.player2.choice?.toUpperCase() || ''}
                  </div>
                </>
              ) : (
                <div className="waiting-player">
                  <div className="empty-avatar">?</div>
                  <div className="waiting-text">Waiting for opponent...</div>
                </div>
              )}
            </div>
          </div>

          {/* Game State Display - REMOVED as requested */}

          {/* Action Buttons */}
          <div className="action-buttons">
            {animationPhase === 'waiting' && !liveGameInfo.player2 && (
              <>
                {/* Only show call bot if you're the game creator */}
                {gameInfo && gameInfo.player1.id === currentUserId ? (
                  <button 
                    className="call-bot-btn" 
                    onClick={handleCallBot}
                    disabled={isCallingBot}
                  >
                    {isCallingBot ? 'Calling...' : 'Call Bot'}
                  </button>
                ) : gameInfo && gameInfo.player1.id !== currentUserId ? (
                  <button 
                    className="join-btn" 
                    onClick={handleJoinGame}
                    disabled={isJoining}
                  >
                    {isJoining ? 'Joining...' : 'Join Game'}
                  </button>
                ) : null}
              </>
            )}
            
            {animationPhase === 'finished' && result && (
              <>
                {getWinnerDisplay()}
                <div className="finished-actions">
                  <button className="play-again-btn" onClick={onClose}>
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    </ErrorBoundary>
  );
};

export default CoinflipPopup;